import { isBefore } from 'date-fns';
import { useState } from 'react';
import { runInBackgroundCallback } from '../../../../../configuration/setup/backgroundActions';
import {
    PutServiceOrderCompletionProps,
    PutServiceOrderStartInfoProps,
    usePostServiceOrderCompletionsMutation,
    usePostServiceOrderStartInfosMutation,
} from '../../../api/serviceOrdersApi';
import { handleQueryError } from '../../../notifications/ErrorNotification';
import { showSuccessNotification } from '../../../notifications/SuccessNotification';
import { isDateStringEmpty, isValidDate } from '../../../sharedComponents/dateHelper';
import { AccessoryType, CollectedAccessories } from '../accessory.types';
import { ServiceOrder, ServiceOrderStatus } from '../serviceOrder.types';
import { getObjectKeyForAccessory } from '../serviceOrderUtils';
import { trimmedOrUndefined } from '../../../api/utils';

export type ServiceOrderExecutionDetailsFormData = {
    accessories: CollectedAccessories;
    generalInformation?: string;
    startedAt: Date | undefined;
    completedAt: Date | undefined;
};

export type ServiceOrderDateField = 'startedAt' | 'completedAt';

type DateParseErrors = Record<ServiceOrderDateField, boolean>;
const initialDateParseErrors: DateParseErrors = {
    startedAt: false,
    completedAt: false,
};

export type SidebarFormFieldError<T> = { type: T };
export type ExecutionDateErrorType = 'dateInFuture' | 'endDateBeforeStartDateError' | 'removedDate' | 'oneMandatory';

type AccessoryTypeErrorType = 'invalidValue';
export type ServiceOrderExecutionFormErrors = {
    [key in AccessoryType]?: SidebarFormFieldError<AccessoryTypeErrorType>;
} & {
    startedAt?: SidebarFormFieldError<ExecutionDateErrorType>;
    completedAt?: SidebarFormFieldError<ExecutionDateErrorType>;
};

export const useServiceOrderExecutionForm = (
    selectedServiceOrder: ServiceOrder | undefined,
    closeSidebar: () => void,
) => {
    const [updateStartInfos, { isLoading: isStartInfoLoading }] = usePostServiceOrderStartInfosMutation();
    const [updateExecutionDetails, { isLoading: isCompletionLoading }] = usePostServiceOrderCompletionsMutation();

    const [formErrors, setFormErrors] = useState<ServiceOrderExecutionFormErrors>({});
    const [formData, setFormData] = useState<ServiceOrderExecutionDetailsFormData>({
        accessories: selectedServiceOrder?.vehicle.collectedAccessories ?? {},
        generalInformation: selectedServiceOrder?.generalInformation,
        startedAt: selectedServiceOrder?.startedAt,
        completedAt: selectedServiceOrder?.completedAt,
    });
    const [dateParseErrors, setDateParseErrors] = useState<DateParseErrors>(initialDateParseErrors);

    const setDateParseError = (field: ServiceOrderDateField, hasParseError: boolean) => {
        setDateParseErrors((current) => ({ ...current, [field]: hasParseError }));
    };

    const validateForm = (): boolean => {
        if (selectedServiceOrder === undefined) {
            return false;
        }

        const newFormErrors: ServiceOrderExecutionFormErrors = {};

        const withoutCompletionDate =
            isDateStringEmpty(formData.completedAt) && selectedServiceOrder?.completedAt === undefined;

        const allAccessoriesAreValid =
            withoutCompletionDate || validateAccessoriesRequired(formData, selectedServiceOrder, newFormErrors);

        newFormErrors.startedAt = validateStartedAt(formData, selectedServiceOrder);
        newFormErrors.completedAt = validateCompletedAt(formData, selectedServiceOrder.status);

        setFormErrors(newFormErrors);
        const hasParseError = Object.values(dateParseErrors).some(Boolean);
        const hasBusinessError = Object.values(newFormErrors).some((error) => error !== undefined);

        return allAccessoriesAreValid && !hasParseError && !hasBusinessError;
    };

    const handleStartInfoUpdate = runInBackgroundCallback(async () => {
        if (validateForm() && selectedServiceOrder !== undefined) {
            try {
                const executionDetailsPayload = getStartInfoPayload(selectedServiceOrder, formData);
                await updateStartInfos(executionDetailsPayload).unwrap();
                showSuccessNotification('outboundPortal.notification.serviceOrder.start.info');
                closeSidebar();
            } catch (error) {
                handleQueryError(error);
            }
        }
    });

    const handleCompletionUpdate = runInBackgroundCallback(async () => {
        if (validateForm() && selectedServiceOrder !== undefined) {
            try {
                const executionDetailsPayload = getCompletionPayload(selectedServiceOrder, formData);
                await updateExecutionDetails(executionDetailsPayload).unwrap();
                showSuccessNotification('outboundPortal.notification.serviceOrder.completion.success');
                closeSidebar();
            } catch (error) {
                handleQueryError(error);
            }
        }
    });

    return {
        formErrors,
        executionDetails: formData,
        setExecutionDetails: setFormData,
        setDateParseError,
        hasDateParseError: Object.values(dateParseErrors).some(Boolean),
        handleSaveServiceOrder: isDateStringEmpty(formData.completedAt)
            ? handleStartInfoUpdate
            : handleCompletionUpdate,
        isExecutionDetailsLoading: isCompletionLoading || isStartInfoLoading,
    };
};

const validateStartedAt = (
    { startedAt: startedAtFormValue }: ServiceOrderExecutionDetailsFormData,
    serviceOrder: ServiceOrder,
): SidebarFormFieldError<ExecutionDateErrorType> | undefined => {
    if (isDateStringEmpty(startedAtFormValue) && serviceOrder.startedAt !== undefined) {
        return { type: 'removedDate' };
    }

    return undefined;
};

const validateCompletedAt = (
    { startedAt: startedAtFormValue, completedAt: completedAtFormValue }: ServiceOrderExecutionDetailsFormData,
    serviceOrderStatus: ServiceOrderStatus,
): SidebarFormFieldError<ExecutionDateErrorType> | undefined => {
    const startedAtIsEmpty = isDateStringEmpty(startedAtFormValue);
    const completedAtIsEmpty = isDateStringEmpty(completedAtFormValue);

    if (serviceOrderStatus === ServiceOrderStatus.COMPLETED && completedAtIsEmpty) {
        return { type: 'removedDate' };
    }

    if (startedAtIsEmpty && completedAtIsEmpty) {
        return { type: 'oneMandatory' };
    }

    if (completedAtFormValue === undefined) {
        return undefined;
    }

    if (completedAtFormValue > new Date()) {
        return { type: 'dateInFuture' };
    }

    if (isValidDate(startedAtFormValue) && isBefore(completedAtFormValue, startedAtFormValue)) {
        return { type: 'endDateBeforeStartDateError' };
    }

    return undefined;
};

export const validateAccessoriesRequired = (
    executionDetails: ServiceOrderExecutionDetailsFormData,
    selectedServiceOrder: ServiceOrder,
    newFormErrors: ServiceOrderExecutionFormErrors,
) => {
    let allAccessoriesAreValid = true;
    selectedServiceOrder.vehicle.requestedAccessories.forEach((requestedAccessory: AccessoryType) => {
        const accessoryValue = executionDetails.accessories[getObjectKeyForAccessory(requestedAccessory)];
        if (accessoryValue === undefined || accessoryValue < 0) {
            newFormErrors[requestedAccessory] = { type: 'invalidValue' };
            allAccessoriesAreValid = false;
        }
    });

    return allAccessoriesAreValid;
};

const getStartInfoPayload = (
    selectedServiceOrder: ServiceOrder,
    executionDetails: ServiceOrderExecutionDetailsFormData,
): PutServiceOrderStartInfoProps => ({
    serviceOrderId: selectedServiceOrder.id,
    startInfo: {
        type: selectedServiceOrder.type,
        vehicleId: selectedServiceOrder.vehicle.id,
        startedAt: executionDetails.startedAt as Date,
        generalInformation: trimmedOrUndefined(executionDetails.generalInformation),
        collectedAccessories: executionDetails.accessories,
    },
});

const getCompletionPayload = (
    selectedServiceOrder: ServiceOrder,
    executionDetails: ServiceOrderExecutionDetailsFormData,
): PutServiceOrderCompletionProps => ({
    serviceOrderId: selectedServiceOrder.id,
    completion: {
        type: selectedServiceOrder.type,
        vehicleId: selectedServiceOrder.vehicle.id,
        startedAt: executionDetails.startedAt,
        completedAt: executionDetails.completedAt as Date,
        generalInformation: trimmedOrUndefined(executionDetails.generalInformation),
        collectedAccessories: executionDetails.accessories,
    },
});
